import { Box, Chip, CircularProgress } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import TeacherPayoutReports from 'src/mentor/components/TeacherPayoutReports/TeacherPayoutReports'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { getMinimalMentorData } from 'src/supportPortal/views/therapists/util'

export default function TherapistPayoutReports() {
  const { mentorSlug } = useParams()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [currentTherapist, setCurrentTherapist] = useState()
  const [associatedClinics, setAssociatedClinics] = useState([])
  const [allEventTypes, setAllEventTypes] = useState([])
  const scopedClinicParams = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return {
      clinicBrandId: params.get('clinicBrandId') || params.get('clinic-brand-id') || '',
      clinicLocationId: params.get('clinicLocationId') || params.get('clinic-location-id') || '',
    }
  }, [location.search])
  const scopedClinicBrandData = useMemo(() => {
    if (!scopedClinicParams?.clinicBrandId) {
      return null
    }
    try {
      const storedBrand = JSON.parse(sessionStorage.getItem('supportClinicBrandData') || 'null')
      return storedBrand?.id === scopedClinicParams.clinicBrandId ? storedBrand : null
    } catch {
      return null
    }
  }, [scopedClinicParams?.clinicBrandId])
  const scopedClinicLocationData = useMemo(() => {
    if (!scopedClinicParams?.clinicLocationId) {
      return null
    }
    try {
      const storedClinic = JSON.parse(sessionStorage.getItem('supportClinicData') || 'null')
      return storedClinic?.id === scopedClinicParams.clinicLocationId ? storedClinic : null
    } catch {
      return null
    }
  }, [scopedClinicParams?.clinicLocationId])
  const scopedSubHeading = useMemo(() => {
    if (!scopedClinicParams?.clinicBrandId) {
      return undefined
    }
    const brandLabel = scopedClinicBrandData?.title || 'Selected clinic brand'
    if (scopedClinicParams?.clinicLocationId) {
      return `${brandLabel} • ${scopedClinicLocationData?.title || 'Selected clinic'}`
    }
    return brandLabel
  }, [
    scopedClinicBrandData?.title,
    scopedClinicLocationData?.title,
    scopedClinicParams?.clinicBrandId,
    scopedClinicParams?.clinicLocationId,
  ])
  const fixedScopeParams = useMemo(
    () => ({
      ...(scopedClinicParams?.clinicBrandId
        ? { 'clinic-brand-id': scopedClinicParams.clinicBrandId }
        : {}),
      ...(scopedClinicParams?.clinicLocationId
        ? { 'clinic-location-id': scopedClinicParams.clinicLocationId }
        : {}),
    }),
    [scopedClinicParams?.clinicBrandId, scopedClinicParams?.clinicLocationId],
  )

  async function getSelectedTherapist() {
    const resp = await getMinimalMentorData(
      mentorSlug,
      true,
      `/api/secure/cc/support/therapists/by-slug?mentor-slug=${mentorSlug}`,
    )
    setCurrentTherapist(resp)
  }

  async function getAllEventTypes() {
    const resp = await getSPDataWithParams(`/api/secure/cc/support/reference/event-types`, {})
    if (resp.status === 200) {
      setAllEventTypes(resp?.data?.sort((a, b) => a?.title?.localeCompare(b?.title)))
    }
  }

  async function getAssociatedBrands() {
    const resp = await getSPDataWithParams(
      '/api/secure/cc/support/therapists/clinic-associations',
      { 'mentor-slug': mentorSlug },
      undefined,
      undefined,
      true,
    )
    if (resp && isCommonResponseSuccessfulV2(resp)) {
      const dat = getDataMapFromCommonResponse(resp)?.clinic
      setAssociatedClinics(dat)
    }
  }
  async function setup() {
    setLoading(true)
    await Promise.all([getSelectedTherapist(), getAssociatedBrands(), getAllEventTypes()])
    setLoading(false)
  }
  useEffect(() => {
    setup()
  }, [])

  return (
    <div className="d-flex flex-column h-100">
      <SupportHeader
        heading={`${currentTherapist?.firstName} Payout Reports`}
        subHeading={scopedSubHeading}
      />
      {(scopedClinicParams?.clinicBrandId || scopedClinicParams?.clinicLocationId) && (
        <Box sx={{ px: 3, pt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {scopedClinicParams?.clinicBrandId && (
            <Chip
              label={`Clinic Brand: ${scopedClinicBrandData?.title || 'Selected brand'}`}
              color="primary"
              variant="outlined"
            />
          )}
          {scopedClinicParams?.clinicLocationId && (
            <Chip
              label={`Clinic Location: ${scopedClinicLocationData?.title || 'Selected clinic'}`}
              color="secondary"
              variant="outlined"
            />
          )}
        </Box>
      )}
      {currentTherapist ? (
        <TeacherPayoutReports
          reportListApiSignature={'/api/secure/cc/support/payout-reports/mentor/paginated'}
          summaryReportApiSignature={'/api/secure/cc/support/payout-reports/mentor/summary'}
          allEventTypes={allEventTypes}
          reportsListingApiParams={{
            'teacher-id': currentTherapist?.id,
            ...fixedScopeParams,
          }}
          summaryReportApiParams={{
            'teacher-id': currentTherapist?.id,
            ...fixedScopeParams,
          }}
          bookedEventLogApiUrl="/api/secure/cc/support/events/logs/get"
          // eventType={eventType}
          //   thisClinicBrandData={selectedClinicBrand}
          hideBackButton
          showSummaryReport
          useLocalDateFormatForApiCalls
          clinicAssociationData={associatedClinics}
          showAdminActions
        />
      ) : (
        <div className="d-flex justify-content-center align-items-center h-100 w-100">
          <CircularProgress />
        </div>
      )}
    </div>
  )
}
